<template>
    <section
        class="workforce-lane"
        :class="{
            'workforce-lane--drop-enabled': canDrop,
            'workforce-lane--drop-active': dragOver,
            'workforce-lane--scrollable': scrollable,
            'workforce-lane--fluid': fluid,
            'workforce-lane--compact': compact,
            'workforce-lane--grid-cards': gridCards,
            'workforce-lane--collapsed': collapsible && collapsed,
            'workforce-lane--expanded': expanded,
            'workforce-lane--mobile-focused': mobileListOpen
        }"
        @keydown.esc.stop="closeMobileList"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
    >
        <header class="workforce-lane__header">
            <div class="workforce-lane__header-top">
                <div
                    class="workforce-lane__title-row"
                    :class="{
                        'workforce-lane__title-row--placement': isPlacementLane
                    }"
                >
                    <i
                        v-if="icon"
                        :class="icon"
                        class="workforce-lane__icon"
                        aria-hidden="true"
                    ></i>
                    <div
                        v-if="reorderable"
                        class="workforce-lane__title workforce-lane__title--reorderable"
                        role="button"
                        tabindex="0"
                        draggable="true"
                        :aria-label="reorderAriaLabel"
                        :title="reorderAriaLabel"
                        @click.stop
                        @dragstart.stop="onReorderDragStart"
                        @dragend.stop="$emit('reorder-dragend')"
                        @keydown="onReorderKeydown"
                    >
                        {{ title }}
                    </div>
                    <h3 v-else class="workforce-lane__title">{{ title }}</h3>
                    <span class="workforce-lane__counters">
                        <span
                            v-b-tooltip.hover.html
                            class="workforce-lane__count"
                            :title="headcountTooltip"
                            :aria-label="headcountAriaLabel"
                        >
                            {{ headcountBreakdown.total }}
                        </span>
                        <span
                            v-if="plannedEmployeeCount"
                            class="workforce-lane__planned-count"
                            :title="plannedEmployeeCountTitle"
                        >
                            <i class="far fa-calendar-alt" aria-hidden="true"></i>
                            +{{ plannedEmployeeCount }}
                        </span>
                    </span>
                </div>
                <div
                    v-if="mobileListOpen || isObjectLane || canAssign || collapsible"
                    class="workforce-lane__header-actions"
                >
                    <button
                        v-if="mobileListOpen"
                        ref="mobileBackButton"
                        type="button"
                        class="workforce-lane__mobile-back"
                        @click="closeMobileList"
                    >
                        <i class="fas fa-arrow-left" aria-hidden="true"></i>
                        Назад к доске
                    </button>
                    <button
                        v-if="!mobileListOpen && isPlacementLane && !mobile"
                        type="button"
                        class="workforce-lane__expand"
                        :aria-label="expandButtonLabel"
                        :title="expandButtonLabel"
                        :aria-expanded="expanded ? 'true' : 'false'"
                        @click.stop="expanded = !expanded"
                    >
                        <i
                            :class="expanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'"
                            aria-hidden="true"
                        ></i>
                    </button>
                    <button
                        v-if="!mobileListOpen && canAssign"
                        type="button"
                        class="workforce-lane__assign"
                        :title="assignButtonTitle"
                        @click="$emit('assign', target)"
                    >
                        <i class="fas fa-plus" aria-hidden="true"></i>
                        <span>Назначить</span>
                    </button>
                    <button
                        v-if="!mobileListOpen && collapsible"
                        type="button"
                        class="workforce-lane__collapse"
                        :title="collapsed ? 'Развернуть блок' : 'Свернуть блок'"
                        :aria-label="collapsed ? 'Развернуть блок' : 'Свернуть блок'"
                        :aria-expanded="collapsed ? 'false' : 'true'"
                        @click="$emit('toggle-collapsed')"
                    >
                        <i
                            :class="collapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
                            aria-hidden="true"
                        ></i>
                    </button>
                </div>
            </div>
            <div
                v-if="subtitle && !(collapsible && collapsed) && !mobileListOpen"
                class="workforce-lane__subtitle"
            >
                {{ subtitle }}
            </div>
        </header>

        <div v-show="!(collapsible && collapsed)" class="workforce-lane__cards">
            <employee-board-card
                v-for="employee in visibleEmployees"
                :key="employeeKey(employee)"
                :employee="employee"
                :can-move="canMoveEmployee(employee)"
                :mobile="mobile"
                :compact="compact"
                :move-label="moveLabel"
                :allow-reserve-move="allowReserveMove"
                :planned-move-pending="plannedMovePending"
                :search-query="searchQuery"
                :dragging-employee-id="draggingEmployeeId"
                :dragging-assignment-id="draggingAssignmentId"
                @open="openEmployee"
                @move="moveEmployee"
                @confirm-planned-move="
                    $emit('confirm-planned-move', $event)
                "
                @contextmenu="onContextMenu"
                @dragstart="$emit('dragstart', $event)"
                @dragend="$emit('dragend')"
            ></employee-board-card>

            <div v-if="!displayEmployees.length" class="workforce-lane__empty">
                {{ emptyText }}
            </div>

            <button
                v-if="mobilePreviewActive"
                type="button"
                class="workforce-lane__show-all"
                @click.stop="openMobileList"
            >
                <span>
                    Показано {{ visibleEmployees.length }} из
                    {{ displayEmployees.length }}
                </span>
                <strong>
                    Показать всех
                    <i class="fas fa-expand-alt" aria-hidden="true"></i>
                </strong>
            </button>
        </div>
    </section>
</template>

<script>
import EmployeeBoardCard from './EmployeeBoardCard.vue';

export default {
    name: 'EmployeeBoardLane',
    components: {
        EmployeeBoardCard
    },
    props: {
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        },
        employees: {
            type: Array,
            default: () => []
        },
        plannedEmployees: {
            type: Array,
            default: () => []
        },
        headcountEmployees: {
            type: Array,
            default: null
        },
        target: {
            type: Object,
            default: null
        },
        canMove: {
            type: Boolean,
            default: false
        },
        allowDrop: {
            type: Boolean,
            default: true
        },
        allowAssign: {
            type: Boolean,
            default: false
        },
        allowReserveMove: {
            type: Boolean,
            default: false
        },
        scrollable: {
            type: Boolean,
            default: false
        },
        fluid: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: false
        },
        gridCards: {
            type: Boolean,
            default: false
        },
        collapsible: {
            type: Boolean,
            default: false
        },
        collapsed: {
            type: Boolean,
            default: false
        },
        mobile: {
            type: Boolean,
            default: false
        },
        searchQuery: {
            type: String,
            default: ''
        },
        draggingEmployeeId: {
            type: [String, Number],
            default: null
        },
        draggingAssignmentId: {
            type: [String, Number],
            default: null
        },
        plannedMovePending: {
            type: Boolean,
            default: false
        },
        reorderable: {
            type: Boolean,
            default: false
        },
        reorderLabel: {
            type: String,
            default: ''
        },
        emptyText: {
            type: String,
            default: 'Нет сотрудников'
        }
    },
    data() {
        return {
            dragDepth: 0,
            expanded: false,
            mobileListOpen: false,
            bodyScrollLock: null
        };
    },
    computed: {
        isObjectLane() {
            return Boolean(this.target && this.target.type === 'object');
        },
        isObjectGroupLane() {
            return Boolean(this.target && this.target.type === 'object_group');
        },
        isPlacementLane() {
            return this.isObjectLane || this.isObjectGroupLane;
        },
        employeesForHeadcount() {
            const employees = Array.isArray(this.headcountEmployees)
                ? this.headcountEmployees
                : this.employees;
            return employees.filter(employee => (
                employee && employee.workforce_timesheet_projection !== true
            ));
        },
        headcountBreakdown() {
            const total = this.employeesForHeadcount.length;
            const itr = this.employeesForHeadcount.filter(employee => (
                employee && employee.multi_assignment_eligible === true
            )).length;
            return {
                total,
                itr,
                workers: total - itr
            };
        },
        headcountTooltip() {
            const counts = this.headcountBreakdown;
            if (!this.isPlacementLane) {
                return `Количество сотрудников: <strong>${counts.total}</strong>`;
            }
            const heading = this.isObjectGroupLane
                ? 'Количество людей, назначенных на группу'
                : 'Количество людей на объекте';
            return '<div class="text-left">' +
                `<strong>${heading}</strong>` +
                `<div>Всего: <strong>${counts.total}</strong></div>` +
                `<div>ИТР: <strong>${counts.itr}</strong></div>` +
                `<div>Рабочих: <strong>${counts.workers}</strong></div>` +
                '</div>';
        },
        headcountAriaLabel() {
            const counts = this.headcountBreakdown;
            if (!this.isPlacementLane) {
                return `Количество сотрудников: ${counts.total}`;
            }
            const heading = this.isObjectGroupLane
                ? 'Количество людей, назначенных на группу'
                : 'Количество людей на объекте';
            return `${heading}: ${counts.total}. ` +
                `ИТР: ${counts.itr}. Рабочих: ${counts.workers}.`;
        },
        searchActive() {
            return Boolean(String(this.searchQuery || '').trim());
        },
        plannedEmployeeCount() {
            return this.plannedEmployees.length;
        },
        plannedEmployeeCountTitle() {
            return `Активных плановых перемещений: ${this.plannedEmployeeCount}`;
        },
        displayEmployees() {
            return this.employees.concat(this.plannedEmployees);
        },
        mobilePreviewLimit() {
            return 5;
        },
        mobilePreviewActive() {
            return Boolean(
                this.mobile
                && !this.mobileListOpen
                && !this.searchActive
                && this.displayEmployees.length > this.mobilePreviewLimit
            );
        },
        visibleEmployees() {
            return this.mobilePreviewActive
                ? this.displayEmployees.slice(0, this.mobilePreviewLimit)
                : this.displayEmployees;
        },
        moveLabel() {
            return 'Назначения';
        },
        canAssign() {
            return this.canMove &&
                this.allowAssign &&
                this.target &&
                this.isPlacementLane;
        },
        canDrop() {
            return this.canMove && this.allowDrop && !this.mobile && Boolean(this.target);
        },
        dragOver() {
            return this.canDrop && this.dragDepth > 0 && this.draggingEmployeeId !== null;
        },
        reorderAriaLabel() {
            return this.reorderLabel
                ? `Перетащить объект «${this.reorderLabel}». Стрелки меняют положение`
                : 'Перетащить объект. Стрелки меняют положение';
        },
        expandButtonLabel() {
            return this.expanded ? 'Свернуть карточку' : 'Развернуть карточку';
        },
        assignButtonTitle() {
            return this.isObjectGroupLane
                ? 'Назначить сотрудника на эту группу'
                : 'Назначить сотрудника на этот объект';
        }
    },
    watch: {
        draggingEmployeeId(value) {
            if (value === null || value === undefined) {
                this.dragDepth = 0;
            }
        },
        mobile(value) {
            if (value) this.expanded = false;
            if (!value && this.mobileListOpen) {
                this.closeMobileList();
            }
        },
        collapsed(value) {
            if (value && this.mobileListOpen) {
                this.closeMobileList();
            }
        }
    },
    beforeDestroy() {
        this.restoreBodyScroll();
    },
    methods: {
        openMobileList() {
            if (!this.mobile || this.mobileListOpen) return;
            this.lockBodyScroll();
            this.mobileListOpen = true;
            this.$nextTick(() => {
                const button = this.$refs.mobileBackButton;
                if (button && typeof button.focus === 'function') {
                    button.focus();
                }
            });
        },
        closeMobileList() {
            if (!this.mobileListOpen && !this.bodyScrollLock) return;
            this.mobileListOpen = false;
            this.restoreBodyScroll();
        },
        lockBodyScroll() {
            if (typeof document === 'undefined' || typeof window === 'undefined') return;
            const body = document.body;
            if (!body || this.bodyScrollLock) return;
            const scrollY = window.pageYOffset ||
                document.documentElement.scrollTop ||
                0;
            this.bodyScrollLock = {
                scrollY,
                overflow: body.style.overflow,
                position: body.style.position,
                top: body.style.top,
                width: body.style.width
            };
            body.style.overflow = 'hidden';
            body.style.position = 'fixed';
            body.style.top = `-${scrollY}px`;
            body.style.width = '100%';
        },
        restoreBodyScroll() {
            if (!this.bodyScrollLock || typeof document === 'undefined') return;
            const body = document.body;
            const lock = this.bodyScrollLock;
            this.bodyScrollLock = null;
            if (body) {
                body.style.overflow = lock.overflow;
                body.style.position = lock.position;
                body.style.top = lock.top;
                body.style.width = lock.width;
            }
            if (typeof window !== 'undefined') {
                window.scrollTo(0, lock.scrollY);
            }
        },
        employeeKey(employee) {
            const employeeId = employee.employee_id || employee.id;
            if (employee.workforce_timesheet_projection === true) {
                const factKey = employee.timesheet_fact && employee.timesheet_fact.key ||
                    'timesheet';
                return `${employeeId}:timesheet:${factKey}`;
            }
            if (employee.workforce_planned_projection === true) {
                const plannedMoveId = employee.planned_move &&
                    employee.planned_move.id || 'planned';
                return `${employeeId}:planned:${plannedMoveId}`;
            }
            const assignment = employee.assignment ||
                employee.assignment_context ||
                {};
            const assignmentId = assignment.assignment_id || assignment.id;
            const kind = assignment.kind ||
                assignment.assignment_kind ||
                (assignment.is_primary === true ? 'primary' : 'additional');
            const targetType = assignment.target_type || assignment.type || 'none';
            const targetId = assignment.target_id ||
                assignment.building_id ||
                assignment.object_group_id ||
                'common';
            return `${employeeId}:${assignmentId || `${kind}:${targetType}:${targetId}`}`;
        },
        openEmployee(employee) {
            this.$emit('open', employee);
        },
        moveEmployee(employee) {
            if (this.mobileListOpen) this.closeMobileList();
            this.$emit('move', employee);
        },
        canMoveEmployee(employee) {
            return this.canMove && employee.can_move !== false;
        },
        onContextMenu(event, employee) {
            this.$emit('contextmenu', event, employee);
        },
        onDragEnter() {
            if (this.canDrop) {
                this.dragDepth += 1;
            }
        },
        onDragOver(event) {
            if (this.canDrop && event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
        },
        onDragLeave() {
            if (this.dragDepth > 0) {
                this.dragDepth -= 1;
            }
        },
        onDrop() {
            const shouldDrop = this.canDrop && this.draggingEmployeeId !== null;
            this.dragDepth = 0;
            if (shouldDrop) {
                this.$emit('drop', this.target);
            }
        },
        onReorderDragStart(event) {
            if (!this.reorderable) {
                event.preventDefault();
                return;
            }
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData(
                    'application/x-argus-workforce-location',
                    String(this.target && this.target.id || '')
                );
                event.dataTransfer.setData(
                    'text/plain',
                    String(this.target && this.target.id || '')
                );
            }
            this.$emit('reorder-dragstart', event);
        },
        onReorderKeydown(event) {
            if (!this.reorderable) return;
            const steps = {
                ArrowLeft: -1,
                ArrowUp: -1,
                ArrowRight: 1,
                ArrowDown: 1
            };
            if (!Object.prototype.hasOwnProperty.call(steps, event.key)) return;
            event.preventDefault();
            event.stopPropagation();
            this.$emit('reorder-step', steps[event.key]);
        }
    }
};
</script>

<style scoped>
.workforce-lane {
    display: flex;
    flex: 0 0 270px;
    flex-direction: column;
    min-width: 270px;
    max-width: 270px;
    min-height: 180px;
    overflow: hidden;
    border: 2px solid #c5d1dc;
    border-radius: 11px;
    background: #f2f6f9;
    box-shadow: 0 3px 9px rgba(35, 53, 72, 0.09);
    transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}

.workforce-lane--fluid {
    flex-basis: auto;
    width: 100%;
    min-width: 0;
    max-width: none;
}

.workforce-lane--drop-enabled {
    border-color: #9fb4c7;
    border-style: dashed;
}

.workforce-lane--drop-active {
    border-color: #2878c7;
    background: #eef6ff;
    box-shadow:
        inset 0 0 0 2px rgba(40, 120, 199, 0.14),
        0 4px 12px rgba(40, 120, 199, 0.14);
}

.workforce-lane--collapsed {
    min-height: 0;
}

.workforce-lane--collapsed .workforce-lane__header {
    border-bottom: 0;
}

.workforce-lane__header {
    padding: 11px 12px 9px;
    border-bottom: 2px solid #cbd6e0;
    background: #fff;
}

.workforce-lane__header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.workforce-lane__header-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
}

.workforce-lane__expand {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #b9c8d7;
    border-radius: 6px;
    background: #fff;
    color: #60778e;
}

.workforce-lane__expand:hover,
.workforce-lane__expand:focus {
    border-color: #6d9fce;
    background: #edf6ff;
    color: #2368a8;
}

.workforce-lane__collapse {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #c5d1dc;
    border-radius: 6px;
    background: #fff;
    color: #60778e;
}

.workforce-lane__collapse:hover,
.workforce-lane__collapse:focus {
    border-color: #7fa8cf;
    background: #edf6ff;
    color: #2368a8;
}

.workforce-lane__title-row {
    display: flex;
    flex: 1 1 0;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

.workforce-lane__title-row--placement {
    align-items: flex-start;
}

.workforce-lane__title-row--placement .workforce-lane__icon {
    margin-top: 4px;
}

.workforce-lane__title-row--placement .workforce-lane__title {
    order: 2;
}

.workforce-lane__title-row--placement .workforce-lane__counters {
    order: 1;
    margin-top: 1px;
}

.workforce-lane__icon {
    color: #65798e;
    font-size: 0.82rem;
}

.workforce-lane__title {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #2b3a49;
    font-size: 0.91rem;
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
    text-align: left;
    white-space: normal;
}

.workforce-lane__title--reorderable {
    cursor: grab;
    user-select: none;
}

.workforce-lane__title--reorderable:hover,
.workforce-lane__title--reorderable:focus {
    color: #2368a8;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
}

.workforce-lane__title--reorderable:active {
    cursor: grabbing;
}

.workforce-lane__assign {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
    padding: 4px 7px;
    border: 1px solid #abc6e1;
    border-radius: 6px;
    background: #fff;
    color: #2368a8;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.2;
}

.workforce-lane__assign:hover,
.workforce-lane__assign:focus {
    border-color: #6d9fce;
    background: #edf6ff;
}

.workforce-lane__mobile-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    border: 1px solid #8fb4d8;
    border-radius: 7px;
    background: #fff;
    color: #245f98;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-lane__count {
    min-width: 23px;
    padding: 2px 6px;
    border-radius: 999px;
    background: #e5ebf2;
    color: #526577;
    font-size: 0.72rem;
    font-weight: 700;
    text-align: center;
    cursor: help;
}

.workforce-lane__count:hover {
    background: #d7e5f2;
    color: #315d83;
}

.workforce-lane__counters {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
}

.workforce-lane__title-row--placement .workforce-lane__counters {
    flex-direction: column;
    gap: 3px;
}

.workforce-lane__planned-count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 22px;
    padding: 2px 7px;
    border: 1px dashed #8fa1b3;
    border-radius: 999px;
    background: #f1f4f7;
    color: #637589;
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-lane__subtitle {
    margin-top: 3px;
    color: #788695;
    font-size: 0.73rem;
    line-height: 1.25;
}

.workforce-lane__cards {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 8px;
    min-height: 108px;
    padding: 9px;
}

.workforce-lane--grid-cards .workforce-lane__cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(205px, 1fr));
    align-content: start;
}

.workforce-lane--grid-cards .workforce-lane__empty {
    grid-column: 1 / -1;
}

.workforce-lane--compact .workforce-lane__header {
    padding: 8px 10px 7px;
}

.workforce-lane--compact .workforce-lane__cards {
    gap: 6px;
    padding: 7px;
}

.workforce-lane--compact .workforce-lane__subtitle {
    margin-top: 2px;
}

.workforce-lane--scrollable .workforce-lane__cards {
    max-height: min(68vh, 760px);
    overflow-y: auto;
    overscroll-behavior-y: auto;
}

.workforce-lane--expanded .workforce-lane__cards {
    max-height: none !important;
    overflow-y: visible !important;
}

.workforce-lane__show-all {
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-column: 1 / -1;
    gap: 12px;
    width: 100%;
    padding: 9px 10px;
    border: 1px solid #aac5df;
    border-radius: 8px;
    background: #fff;
    color: #587087;
    font-size: 0.72rem;
    text-align: left;
    touch-action: manipulation;
}

.workforce-lane__show-all strong {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    color: #2468a8;
    font-size: 0.76rem;
}

.workforce-lane__empty {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
    min-height: 86px;
    padding: 16px;
    color: #8b96a1;
    font-size: 0.78rem;
    text-align: center;
}

.workforce-lane--mobile-focused {
    position: fixed;
    z-index: 1085;
    inset: 0;
    width: auto;
    min-width: 0;
    max-width: none;
    height: auto;
    min-height: 0;
    border: 0;
    border-radius: 0;
    background: #f2f6f9;
    box-shadow: none;
}

.workforce-lane--mobile-focused .workforce-lane__header {
    flex: 0 0 auto;
    padding: calc(9px + env(safe-area-inset-top)) 10px 9px;
    border-bottom: 1px solid #cbd7e2;
    background: #fff;
    box-shadow: 0 2px 8px rgba(31, 52, 73, 0.1);
}

.workforce-lane--mobile-focused .workforce-lane__title {
    font-size: 0.82rem;
}

.workforce-lane--mobile-focused .workforce-lane__cards {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none !important;
    overflow-y: auto !important;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    overscroll-behavior-y: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
}

@media (max-width: 767.98px) {
    .workforce-lane {
        flex-basis: auto;
        width: 100%;
        min-width: 0;
        max-width: none;
    }

    .workforce-lane--scrollable:not(.workforce-lane--mobile-focused)
    .workforce-lane__cards {
        max-height: none !important;
        overflow-y: visible !important;
        overscroll-behavior-y: auto;
        touch-action: pan-y;
    }

    .workforce-lane__assign span {
        display: none;
    }

    .workforce-lane__assign {
        width: 28px;
        height: 28px;
        justify-content: center;
        padding: 0;
    }
}
</style>
